<template>
  <VirtualList
    direction="horizontal"
    :items="dateItems"
    :itemSize="itemSize"
    ref="list"
  >
    <template slot="header">
      <div class="c-virtual-list__head keyword" style="position:sticky;">
        <div class="c-virtual-list__col check"></div>
        <ThSort
          v-model="temp.sort"
          name="keyword"
          orderby="asc"
          tag="div"
          type="string"
          class="c-virtual-list__col keyword c-text--center"
          @change="change"
        >
          キーワード
        </ThSort>
        <ThSort
          v-model="temp.sort"
          name="url"
          orderby="asc"
          tag="div"
          type="string"
          class="c-virtual-list__col url c-text--center"
          @change="change"
        >
          URL
        </ThSort>
        <ThSort
          v-model="temp.sort"
          name="taskDoneDate"
          orderby="desc"
          tag="div"
          type="date"
          class="c-virtual-list__col task c-text--center"
          @change="change"
        >
          直近のタスク
        </ThSort>
      </div>
      <ListItemKeyword
        v-for="item in items"
        :key="item.id"
        :checked="checked"
        :dates="dates"
        :item="item"
        :project="project"
        :selectedId="selectedId"
        @check="updateChecked"
        @select="select"
        @displayItem="displayItem"
      />
      <div v-if="!hasData" class="c-virtual-list__guide">
        <div class="c-virtual-list__col c-text--center">
          条件に合うデータがありません。
        </div>
      </div>
    </template>
    <template slot="before">
      <div class="c-virtual-list__head months">
        <div
          v-for="(m, index) in dates.months"
          :key="index"
          class="c-virtual-list__col month"
          :style="{ 'max-width': m.width, 'min-width': m.width }"
        >
          <EllipsisPopover placement="top" size="sm">
            {{ m.label }}
          </EllipsisPopover>
        </div>
      </div>
      <div class="c-virtual-list__head days">
        <div
          v-for="(d, index) in dates.days"
          :key="index"
          class="c-virtual-list__col day"
        >
          {{ d.label }}
        </div>
      </div>
    </template>
    <template v-slot="{ item }">
      <div :key="item.id" class="c-virtual-list__col position">
        <ListItemKeywordData
          v-for="(position, index) in item.positions"
          :key="index"
          :date="item.date"
          :item="position"
        />
      </div>
    </template>
  </VirtualList>
</template>

<script>
import { CALENDAR } from '@/constants';
import EllipsisPopover from '@/components/EllipsisPopover';
import ListItemKeyword from '@/components/siteImportantKeywordGA4/ListItemKeyword';
import ListItemKeywordData from '@/components/siteImportantKeywordGA4/ListItemKeywordData';
import ThSort from '@/components/ThSort';
import VirtualList from '@/components/VirtualList';
import utils from '@/utils';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    period: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    selectedId: {
      type: String,
      required: true
    },
    sort: {
      type: Object,
      required: true
    }
  },
  components: {
    EllipsisPopover,
    ListItemKeyword,
    ListItemKeywordData,
    ThSort,
    VirtualList
  },
  data() {
    return {
      itemSize: 30,
      temp: {
        sort: Object.assign({}, this.sort)
      }
    };
  },
  computed: {
    dateItems() {
      const items = this.items;
      return this.dates.days.map(day => {
        const key = utils.formatDate(day.date, 'YYYY-MM-DD');
        const positions = items.map(item => {
          let position = null;
          // 直近3日間の順位はnull = データ取得中扱いにする
          if (!item.isLoading && day.date <= CALENDAR.gsc.maxDate) {
            // 順位辞書がある場合
            if (item.dictionary) {
              if (item.dictionary[key] !== undefined) {
                position = item.dictionary[key];
              }
            } else if (item.dates) {
              // 順位辞書がない場合はitem.datesの中で日付が同じものを探す
              const time1 = day.date.getTime();
              const result = item.dates.find(item => {
                const time2 = utils.getDateResetHours(item.date).getTime();
                return time2 === time1;
              });
              if (result) {
                position = result.position;
              }
            }
          }

          let type = null;
          let isProposal = null;
          if (item.doneTasks) {
            if (item.doneTasks[key] !== undefined) {
              type = item.doneTasks[key].type;
              isProposal = item.doneTasks[key].isProposal;
            }
          }

          return {
            id: item.id,
            position: position,
            isLoading: item.isLoading,
            isProposal: isProposal,
            type: type
          };
        });
        return {
          id: key,
          date: day.date,
          positions: positions
        };
      });
    },
    dates() {
      const obj = {
        months: [],
        days: []
      };
      const startDate = new Date(this.period[0]);
      const endDate = new Date(this.period[1]);
      const total = utils.getDays(startDate, endDate);
      const d = new Date(startDate);

      for (let i = 0; i <= total; i++) {
        if (d.getDate() === 1 || i === 0) {
          let lastDate = utils.getEndDate(d, 'month');
          if (lastDate.getTime() > endDate.getTime()) {
            lastDate = new Date(endDate);
          }
          const days = utils.getDays(d, lastDate) + 1;
          const width = days * this.itemSize + 'px';
          obj.months.push({
            days: days,
            label: utils.formatDate(d, 'YYYY.MM'),
            width: width
          });
        }
        obj.days.push({
          date: new Date(d),
          label: utils.formatDate(d, 'D')
        });
        d.setDate(d.getDate() + 1);
      }
      return obj;
    },
    hasData() {
      return this.items.length ? true : false;
    }
  },
  watch: {
    dates: {
      handler() {
        this.$nextTick(() => {
          this.$refs.list.resize();
          this.$refs.list.scrollToEnd();
        });
      },
      immediate: true
    },
    sort: {
      handler(value) {
        this.temp.sort = Object.assign({}, value);
      }
    }
  },
  methods: {
    change() {
      this.$emit('sort', this.temp.sort);
    },
    select(item) {
      this.$emit('select', item);
    },
    updateChecked(checked) {
      this.$emit('check', checked);
    },
    displayItem(id) {
      this.$emit('displayItem', id);
    }
  }
};
</script>
